/**
 * State for the staff-recorded ACH debit authorization every staff-started
 * bank debit needs — see `AchAuthorizationFields`, which renders it. Shared by
 * the "charge a new card/bank account" collector and the saved-method charges
 * in the Contact and Invoice dialogs.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompanyDisplayName } from '@/hooks/settings/useCompanyDisplayName';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import type { StaffChargeAuthorization, StaffChargeAuthorizationChannel } from '@/types/contact';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function buildAuthorizationText(
  contactName: string,
  businessName: string,
  amount: number,
  acceptedAt: string,
  timeZone?: string,
) {
  const date = formatDateTimeInTimeZone(acceptedAt, timeZone, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return `${contactName} authorizes ${businessName} to debit their bank account on file for ${formatCurrency(amount)} on ${date}.`;
}

export type AchAuthorizationState = ReturnType<typeof useAchAuthorization>;

/**
 * `enabled` is whether this charge is a bank debit at all — a card charge
 * carries no authorization and is never held up by one. `debitAmount` is what
 * leaves the customer's account (after any ACH/check discount, plus any tip);
 * null while it is still being worked out, which holds the charge.
 */
export function useAchAuthorization({
  contactName,
  debitAmount,
  enabled,
  amountEntered = true,
  accountKey = null,
  payerNameLoading = false,
  payerNameResolved = true,
}: {
  contactName: string;
  debitAmount: number | null;
  enabled: boolean;
  /** Whether staff have typed an amount yet — no amount is not "still working it out". */
  amountEntered?: boolean;
  /**
   * The saved account being debited. The text says only "their bank account
   * on file", so an authorization taken for one account must not carry over
   * to another: a change here clears it.
   */
  accountKey?: string | null;
  /**
   * The payer's record is in flight. Only that — it chooses the spinner copy in
   * `AchAuthorizationFields`, so a request that has already failed must not be
   * reported here or the spinner never resolves.
   */
  payerNameLoading?: boolean;
  /**
   * Whether the name we have IS the payer's. False when their record failed to
   * load or carries no name, which is not the same as `contactName` being
   * blank: callers fall back to a label so a dialog is never empty, so a name
   * can be present and still not be the payer's. False holds the
   * authorization and shows the actionable "add a name" message rather than a
   * spinner.
   */
  payerNameResolved?: boolean;
}) {
  const companyTimeZone = useCompanyTimezone();
  // The account holder the text names. Never built around a blank name — the
  // stored authorization has to say who gave it.
  const payerName = contactName.trim();
  // The business the customer authorizes to debit their account. The text is
  // stored with the payment and quoted in the customer's copy, so it is never
  // built around a blank or placeholder name: no name, no bank charge.
  const company = useCompanyDisplayName();
  const businessName = company.name;

  const [channel, setChannel] = useState<StaffChargeAuthorizationChannel | null>(null);
  // When staff ticked "the customer authorized this debit" — the moment the
  // authorization was taken, and fixed from then on. The saved-method charge
  // hooks derive their idempotency key from the request body, so a stamp minted
  // per click would make a retry after a lost response a NEW charge (a second
  // debit) instead of a replay of the first.
  //
  // Bound to the exact text staff confirmed: change the amount (or anything
  // else the text names) and the box reads unticked, so a debit the customer
  // never agreed to can't ride on an earlier confirmation. Coming back to the
  // same text (a quote refetch that briefly blanks it) keeps it.
  const [confirmation, setConfirmation] = useState<{ text: string; at: string } | null>(null);

  // Fixed for one authorization session rather than re-derived on every
  // render, so the date named in the text can't drift from the date shown to
  // staff while the dialog is open. A new session — `reset()` (the dialogs
  // call it when they close) or a different account — takes today's date:
  // the dialogs stay mounted with their page, so a per-mount date would name
  // the day the page loaded, not the day of the debit. Rendered in the
  // company's business timezone (never the browser's) so the calendar day
  // matches what appears on the rest of the tenant's documents.
  const [authorizationDate, setAuthorizationDate] = useState(() => new Date().toISOString());

  // Stable, so a dialog can clear it from its own session-reset effect.
  const reset = useCallback(() => {
    setChannel(null);
    setConfirmation(null);
    setAuthorizationDate(new Date().toISOString());
  }, []);
  useEffect(() => {
    reset();
  }, [accountKey, reset]);
  // No text while the payer's record is still loading, even though a caller
  // usually has SOME name to hand. Most of them fall back to a label off the
  // job or invoice so a dialog is never blank, which means `payerName` is
  // non-empty before the real payer is known — and without this the fields
  // render, and staff can accept, an authorization naming the wrong person on a
  // debit that is about to leave a real bank account. The text is stored with
  // the payment and sent to the customer, so it has to name the payer.
  //
  // Settled means: we have a name, it is theirs, and nothing is still on its
  // way to replace it.
  const payerKnown = payerName !== '' && payerNameResolved && !payerNameLoading;
  const text = useMemo(
    () =>
      businessName && debitAmount !== null && payerKnown
        ? buildAuthorizationText(
            payerName,
            businessName,
            debitAmount,
            authorizationDate,
            companyTimeZone,
          )
        : null,
    [payerName, payerKnown, businessName, debitAmount, authorizationDate, companyTimeZone],
  );

  const confirmedAt = text !== null && confirmation?.text === text ? confirmation.at : null;
  const confirmed = confirmedAt !== null;
  function setConfirmed(checked: boolean) {
    setConfirmation(checked && text ? { text, at: new Date().toISOString() } : null);
  }

  const ready = !enabled || (Boolean(text) && Boolean(channel) && confirmed);

  /** The authorization to send with the charge; undefined for a card. */
  function build(): StaffChargeAuthorization | undefined {
    return enabled && channel && text && confirmedAt
      ? { channel, text, acceptedAt: confirmedAt }
      : undefined;
  }

  return {
    company,
    businessName,
    amountKnown: debitAmount !== null,
    amountEntered,
    // `AchAuthorizationFields` keys its account-holder branch on this, then
    // picks the spinner or the "add a name" alert from `payerNameLoading`.
    payerNameKnown: payerKnown,
    payerNameLoading,
    channel,
    setChannel,
    confirmed,
    setConfirmed,
    text,
    ready,
    build,
    reset,
  };
}
